import React, { useState, useEffect } from 'react';
import { whatsappService, doctorService, handleApiError } from '../utils/apiClient';
import { config } from '../config/config';
import { trackWhatsAppClick, createPatientHash } from '../utils/analytics';

const WhatsAppBooking = ({ 
  previousStep, 
  resetApp,
  appState, 
  setError,
  setLoading 
}) => {
  const [bookingState, setBookingState] = useState({
    whatsappUrl: '',
    isGenerating: false,
    isGenerated: false,
    bookingTracked: false,
    bookingId: null,
    error: null,
    messagePreview: ''
  });

  const [appointmentDetails, setAppointmentDetails] = useState({
    preferredDate: '',
    preferredTime: '',
    urgency: 'routine',
    additionalNotes: ''
  });

  // Generate WhatsApp URL when component mounts
  useEffect(() => {
    if (!bookingState.isGenerated && appState.selectedDoctor && appState.patientData && appState.analysisResult) {
      generateWhatsAppUrl();
    }
  }, []);

  const generateWhatsAppUrl = () => {
    try {
      setBookingState(prev => ({ ...prev, isGenerating: true }));

      // Generate comprehensive message
      const messageTemplate = config.whatsapp.messageTemplate;
      
      const message = [
        messageTemplate.greeting,
        '',
        messageTemplate.referral,
        '',
        '📋 *Patient Information:*',
        `Name: ${appState.patientData.name}`,
        `Age: ${appState.patientData.age}`,
        `Phone: ${appState.patientData.phone}`,
        '',
        '🔬 *AI Analysis Summary:*',
        `Condition: ${appState.analysisResult.condition}`,
        `Severity: ${appState.analysisResult.severity}`,
        '',
        '💊 *Preliminary Recommendations:*',
        appState.analysisResult.treatment,
        '',
        appointmentDetails.preferredDate && `📅 Preferred Date: ${appointmentDetails.preferredDate}`,
        appointmentDetails.preferredTime && `⏰ Preferred Time: ${appointmentDetails.preferredTime}`,
        appointmentDetails.urgency !== 'routine' && `🚨 Urgency: ${appointmentDetails.urgency}`,
        appointmentDetails.additionalNotes && `📝 Additional Notes: ${appointmentDetails.additionalNotes}`,
        '',
        '📄 *Complete Medical Report:*',
        'Patient has a detailed AI analysis report available for review during consultation.',
        '',
        messageTemplate.request,
        '',
        '⚠️ *Please note:* This is an AI-assisted preliminary analysis. Professional medical evaluation is essential for proper diagnosis and treatment.',
        '',
        `Generated via ${config.pdf.companyName} Dermatology Consultation Platform`
      ].filter(Boolean).join('\n');

      const whatsappUrl = whatsappService.generateBookingUrl(
        appState.selectedDoctor,
        appState.patientData,
        appState.analysisResult
      );

      // Override with our detailed message
      const phoneNumber = appState.selectedDoctor.whatsapp_number || appState.selectedDoctor.phone;
      const cleanPhoneNumber = phoneNumber.replace(/[^\d]/g, '');
      const encodedMessage = encodeURIComponent(message);
      const finalUrl = `https://wa.me/${cleanPhoneNumber}?text=${encodedMessage}`;

      setBookingState(prev => ({
        ...prev,
        isGenerating: false,
        isGenerated: true,
        whatsappUrl: finalUrl,
        messagePreview: message
      }));

    } catch (error) {
      setBookingState(prev => ({
        ...prev,
        isGenerating: false,
        error: 'Failed to generate WhatsApp booking link'
      }));
      setError('Failed to generate WhatsApp booking link');
    }
  };

  const trackAndOpenWhatsApp = async () => {
    setLoading(true);
    
    try {
      // Enhanced analytics tracking (HIPAA compliant)
      const patientHash = createPatientHash(appState.patientData);
      const doctorData = {
        id: appState.selectedDoctor.id,
        clinic_id: appState.selectedDoctor.clinic_id || null
      };

      // Track WhatsApp click for analytics
      const analyticsResult = await trackWhatsAppClick(doctorData, appState.patientData);

      // Track the appointment booking for commission purposes (legacy)
      if (!bookingState.bookingTracked) {
        try {
          const trackingResponse = await doctorService.trackAppointmentBooking(
            appState.selectedDoctor.id,
            appState.patientData?.name || 'Patient',
            {
              analysisResults: appState.analysisResult ? {
                primaryCondition: appState.analysisResult.overallAssessment?.condition,
                severity: appState.analysisResult.overallAssessment?.severity
              } : null
            }
          );

          if (trackingResponse.success) {
            setBookingState(prev => ({
              ...prev,
              bookingTracked: true,
              bookingId: trackingResponse.trackingId || analyticsResult.trackingId
            }));
          }
        } catch (commissionError) {
          // Continue with analytics tracking even if commission tracking fails
          if (analyticsResult.success) {
            setBookingState(prev => ({
              ...prev,
              bookingTracked: true,
              bookingId: analyticsResult.trackingId
            }));
          }
        }
      }

      // Open WhatsApp
      whatsappService.openWhatsApp(bookingState.whatsappUrl);

    } catch (error) {
      // Still open WhatsApp even if tracking fails
      whatsappService.openWhatsApp(bookingState.whatsappUrl);
    } finally {
      setLoading(false);
    }
  };

  const updateAppointmentDetails = (field, value) => {
    setAppointmentDetails(prev => ({ ...prev, [field]: value }));
    
    // Regenerate URL with updated details
    if (bookingState.isGenerated) {
      setBookingState(prev => ({ ...prev, isGenerated: false }));
      setTimeout(generateWhatsAppUrl, 100);
    }
  };

  const handleStartOver = () => {
    resetApp();
  };

  // Loading state
  if (bookingState.isGenerating) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="card-lg text-center animate-fade-in-up">
          <div className="mb-8">
            <div className="w-20 h-20 mx-auto mb-6">
              <svg className="w-full h-full text-green-600 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Preparing WhatsApp Booking
            </h2>
            <p className="text-gray-600">
              Creating your personalized appointment request message
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (bookingState.error) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="card-lg text-center animate-fade-in-up">
          <div className="mb-8">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Booking Setup Failed
            </h2>
            <p className="text-gray-600 mb-6">
              {bookingState.error}
            </p>
          </div>

          <div className="flex justify-center space-x-4">
            <button onClick={generateWhatsAppUrl} className="btn-primary">
              Try Again
            </button>
            <button onClick={previousStep} className="btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state - Ready to book
  if (bookingState.isGenerated) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="space-y-6 animate-fade-in-up">
          {/* Header */}
          <div className="card-lg text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Book Your Appointment
              </h2>
            </div>
            <p className="text-gray-600">
              Ready to connect with Dr. {appState.selectedDoctor.name} via WhatsApp
            </p>
          </div>

          {/* Selected Doctor Summary */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Doctor</h3>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">Dr. {appState.selectedDoctor.name}</h4>
                <p className="text-primary-600">{appState.selectedDoctor.specialization || 'Dermatologist'}</p>
                <p className="text-gray-600 text-sm">{appState.selectedDoctor.clinic_name}</p>
                <p className="text-gray-500 text-sm">{appState.selectedDoctor.distanceFormatted} away</p>
              </div>
              <div className="text-right">
                <div className="flex items-center text-green-600 text-sm mb-1">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                  </svg>
                  WhatsApp Available
                </div>
                <p className="text-gray-600 text-sm">{appState.selectedDoctor.phone}</p>
              </div>
            </div>
          </div>

          {/* Appointment Preferences */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Appointment Preferences (Optional)</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Preferred Date</label>
                <input
                  type="date"
                  value={appointmentDetails.preferredDate}
                  onChange={(e) => updateAppointmentDetails('preferredDate', e.target.value)}
                  className="form-input"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="form-label">Preferred Time</label>
                <select
                  value={appointmentDetails.preferredTime}
                  onChange={(e) => updateAppointmentDetails('preferredTime', e.target.value)}
                  className="form-input"
                >
                  <option value="">No preference</option>
                  <option value="Morning (9 AM - 12 PM)">Morning (9 AM - 12 PM)</option>
                  <option value="Afternoon (12 PM - 5 PM)">Afternoon (12 PM - 5 PM)</option>
                  <option value="Evening (5 PM - 8 PM)">Evening (5 PM - 8 PM)</option>
                </select>
              </div>

              <div>
                <label className="form-label">Urgency Level</label>
                <select
                  value={appointmentDetails.urgency}
                  onChange={(e) => updateAppointmentDetails('urgency', e.target.value)}
                  className="form-input"
                >
                  <option value="routine">Routine Consultation</option>
                  <option value="urgent">Urgent (Within a week)</option>
                  <option value="emergency">Emergency (ASAP)</option>
                </select>
              </div>

              <div>
                <label className="form-label">Additional Notes</label>
                <textarea
                  value={appointmentDetails.additionalNotes}
                  onChange={(e) => updateAppointmentDetails('additionalNotes', e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Any additional information for the doctor..."
                />
              </div>
            </div>
          </div>

          {/* Message Preview */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Message Preview</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-40 overflow-y-auto">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                {bookingState.messagePreview}
              </pre>
            </div>
            <p className="text-gray-500 text-sm mt-2">
              This message will be pre-filled in WhatsApp for you to send to the doctor
            </p>
          </div>

          {/* Booking Action */}
          <div className="card">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ready to Book Your Appointment</h3>
              <p className="text-gray-600 mb-6">
                Click the button below to open WhatsApp with your pre-filled appointment request
              </p>
              
              <button
                onClick={trackAndOpenWhatsApp}
                className="btn-primary text-lg px-8 py-4 flex items-center space-x-3 mx-auto"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                </svg>
                <span>Open WhatsApp & Book Appointment</span>
              </button>

              <p className="text-gray-500 text-sm mt-4">
                This will open WhatsApp on your device with the message ready to send
              </p>
            </div>
          </div>

          {/* Commission Info */}
          {bookingState.bookingTracked && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-green-800 text-sm">
                  Booking tracked successfully. Reference ID: {bookingState.bookingId}
                </p>
              </div>
            </div>
          )}

          {/* Final Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">What happens next?</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>1. WhatsApp will open with your appointment request</li>
                  <li>2. Send the message to Dr. {appState.selectedDoctor.name}</li>
                  <li>3. The doctor will respond to confirm your appointment</li>
                  <li>4. Bring your downloaded PDF report to the consultation</li>
                  <li>5. Remember: This AI analysis is preliminary - professional diagnosis is essential</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <button
              onClick={previousStep}
              className="btn-secondary"
            >
              ← Previous
            </button>

            <div className="text-center">
              <p className="text-gray-600 text-sm mb-2">
                All done with your consultation!
              </p>
              <button
                onClick={handleStartOver}
                className="text-primary-600 hover:text-primary-700 font-medium text-sm"
              >
                Start New Consultation
              </button>
            </div>

            <div className="w-32"> {/* Spacer for layout balance */}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default state (shouldn't reach here)
  return null;
};

export default WhatsAppBooking;
